import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { isAxiosError } from 'axios';
import { authApi, type User } from '@/lib/api/auth';
import { safeLocalStorage } from '@/lib/safe-storage';
import Cookies from 'js-cookie';

// Auth modal views
type AuthModalView = 'login' | 'verify' | 'callme' | 'telegram';

// Метод верификации
type VerificationMethod = 'flashcall' | 'telegram-code';

// Auth state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthModalOpen: boolean;
  authModalView: AuthModalView;

  // Phone verification state
  verificationPhone: string | null;
  verificationRequestId: string | null;
  verificationMethod: VerificationMethod; // какой метод используется

  // Telegram auth state
  telegramToken: string | null;
  telegramBotUsername: string | null;

  // Hydration flag — критично для SSR/CSR mismatch:
  // на сервере false → на клиенте до rehydrate тоже false → после rehydrate true.
  // Все компоненты, читающие isAuthenticated, должны делать AND с _hasHydrated.
  _hasHydrated: boolean;
  setHasHydrated: (value: boolean) => void;

  // Actions
  setUser: (user: User | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  
  // Modal actions
  openAuthModal: (view?: AuthModalView) => void;
  closeAuthModal: () => void;
  setAuthModalView: (view: AuthModalView) => void;
  
  // Verification actions
  setVerificationData: (phone: string, requestId: string, method?: VerificationMethod) => void;
  setTelegramData: (token: string, botUsername: string) => void;
  clearVerificationData: () => void;

  // Auth actions
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    immer((set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isAuthModalOpen: false,
      authModalView: 'login' as AuthModalView,
      verificationPhone: null,
      verificationRequestId: null,
      verificationMethod: 'flashcall' as VerificationMethod,
      telegramToken: null,
      telegramBotUsername: null,
      _hasHydrated: false,

      setHasHydrated: (value) => {
        set((state) => {
          state._hasHydrated = value;
        });
      },

      setUser: (user) => {
        set((state) => {
          state.user = user;
          state.isAuthenticated = !!user;
        });
      },

      setIsAuthenticated: (value) => {
        set((state) => {
          state.isAuthenticated = value;
        });
      },

      setIsLoading: (value) => {
        set((state) => {
          state.isLoading = value;
        });
      },

      // Modal actions
      openAuthModal: (view = 'login') => {
        set((state) => {
          state.isAuthModalOpen = true;
          state.authModalView = view;
        });
      },

      closeAuthModal: () => {
        set((state) => {
          state.isAuthModalOpen = false;
          state.verificationPhone = null;
          state.verificationRequestId = null;
          state.telegramToken = null;
          state.telegramBotUsername = null;
          state.authModalView = 'login';
        });
      },

      setAuthModalView: (view) => {
        set((state) => {
          state.authModalView = view;
        });
      },

      // Verification actions
      setVerificationData: (phone, requestId, method = 'flashcall') => {
        set((state) => {
          state.verificationPhone = phone;
          state.verificationRequestId = requestId;
          state.verificationMethod = method;
        });
      },

      setTelegramData: (token, botUsername) => {
        set((state) => {
          state.telegramToken = token;
          state.telegramBotUsername = botUsername;
        });
      },

      clearVerificationData: () => {
        set((state) => {
          state.verificationPhone = null;
          state.verificationRequestId = null;
          state.verificationMethod = 'flashcall';
          state.telegramToken = null;
          state.telegramBotUsername = null;
        });
      },

      // Auth actions
      logout: async () => {
        await authApi.logout();
        
        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.verificationPhone = null;
          state.verificationRequestId = null;
          state.telegramToken = null;
          state.telegramBotUsername = null;
        });
      },

      checkAuth: async () => {
        const token = Cookies.get('accessToken');
        
        if (!token) {
          set((state) => {
            state.user = null;
            state.isAuthenticated = false;
          });
          return false;
        }
        
        try {
          const user = await authApi.getProfile();
          
          set((state) => {
            state.user = user;
            state.isAuthenticated = true;
          });
          
          return true;
        } catch (error) {
          // Разлогиниваем ТОЛЬКО когда сервер прямо сказал, что токен негоден.
          // Раньше здесь стоял пустой catch: любая ошибка — упавшая база, таймаут,
          // отвалившийся wifi — стирала токен и выбрасывала из аккаунта. Именно так
          // админа вышибало при открытии тяжёлой аналитики (бэк отдавал 401 при
          // недоступной БД; теперь он отдаёт 503, но сеть и таймауты остаются).
          const status = isAxiosError(error) ? error.response?.status : undefined;
          const tokenRejected = status === 401 || status === 403;

          if (!tokenRejected) {
            // Сессию не трогаем: сервер или сеть подведут — пользователь останется
            // залогинен, а данные подтянутся при следующей удачной попытке.
            return false;
          }

          Cookies.remove('accessToken');

          set((state) => {
            state.user = null;
            state.isAuthenticated = false;
          });

          return false;
        }
      },
    })),
    {
      name: 'aziatochka-auth',
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Помечаем после загрузки из localStorage — гарантирует SSR/CSR consistency.
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Selectors
export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectIsAuthLoading = (state: AuthState) => state.isLoading;
export const selectIsAuthModalOpen = (state: AuthState) => state.isAuthModalOpen;
export const selectAuthModalView = (state: AuthState) => state.authModalView;
export const selectAuthHasHydrated = (state: AuthState) => state._hasHydrated;
// Безопасный селектор — true только после rehydrate.
// Используй везде где `isAuthenticated` влияет на JSX (структура DOM меняется → hydration mismatch).
export const selectIsAuthenticatedSafe = (state: AuthState) =>
  state._hasHydrated && state.isAuthenticated;
